import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {
    addLeadingZero,
    convertStartEndDatesForFile,
    copyObjectArray,
    toCurrency,
} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {
    getSalesComparisonSummaryColumns,
    getSalesComparisonDetailColumns,
    getSalesComparisonCSVHeaders
} from '../shared/salesComparisonReportFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import CircularProgress from 'material-ui/CircularProgress';
import FlatButton from 'material-ui/FlatButton';
import DetailTableIcon from 'material-ui/svg-icons/communication/location-on';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import SummaryTableIcon from 'material-ui/svg-icons/action/date-range';
import Config from '../messages';

/**
 * Class for retrieving and displaying Sales Comparison Summary in a single table
 *
 */
class SalesComparisonReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            allowLocationSelection: true,
            detailComplete: false,
            detailList: [],
            detailLoadedBase: false,
            detailLoadedCompare: false,
            detailLocation: {
                id: null,
                name: null,
                dba_name: null,
                city: null,
                selected: false,
                state: null,
                zipcode: null
            },
            detailTableData: [],
            loadIndex: 0,
            loadingData: false,
            loadingMessage: '',
            reportName: 'Sales Comparison',
            summaryComplete: false,
            summariesLoaded: 0,
            summaryList: [],
            summaryLoadedBase: false,
            summaryLoadedCompare: false,
            summaryTableData: [],
            totalYears: 0
        };
        this.detailPosition = null;
        this.makeDataCalls = this.makeDataCalls.bind(this);
        this.fetchData = this.fetchData.bind(this);
        this.processData = this.processData.bind(this);
        this.handleLocationSelect = this.handleLocationSelect.bind(this);
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        this.makeDataCalls(null);
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.offset !== this.props.offset ||
            nextProps.yearsList !== this.props.yearsList
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                loadIndex: 0,
                detailComplete: false,
                detailList: [],
                detailTableData: [],
                summaryComplete: false,
                summaryList: [],
                summaryTableData: []
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.offset !== this.props.offset ||
            prevProps.yearsList !== this.props.yearsList
        ) {
            this.makeDataCalls(null);
        }
    }

    /* API Calls ------------------------------ */

    makeDataCalls() {
        const yearsList = this.props.yearsList;
        const totalYears = yearsList.length;
        if (totalYears) {
            this.setState({
                loadIndex: 0,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false,
                totalYears: totalYears
            }, () => {
                this.fetchData('base', 0, yearsList[0].start, yearsList[0].end, null);
            });
        }
    }

    makeLocationDataCalls(locationId) {
        const yearsList = this.props.yearsList;
        this.setState({
            loadIndex: 0,
            detailList: [],
            detailTableData: [],
            detailComplete: false,
        }, () => {
            this.fetchData('base', 0, yearsList[0].start, yearsList[0].end, locationId);
        });
    }

    /**
     * Get Sales Comparison data for a specific date range
     *
     * @param yearType
     * @param index
     * @param start
     * @param end
     * @param locationId
     */
    fetchData(yearType, index, start, end, locationId) {

        let errorMessage = Config.fuse.error.error + ' Sales Comparison data.';
        let errorType = 'base';
        let loadingMessage = 'Generating Report...';
        let locationMessage = (locationId) ? ' Location' : '';
        if (yearType === 'base') {
            loadingMessage = 'Generating ' + locationMessage + 'Report for Selected Range...';
        } else {
            let yearsBack = (index === 1) ? '1 Year Back...' : index + ' Years Back...';
            loadingMessage = 'Generating Report for ' + yearsBack;
        }
        this.setState({
            loadingData: true,
            loadingMessage: loadingMessage,
        });

        // Retrieve data for selected range (base) or range to compare
        let params = {
            start_date: start,
            end_date: end,
            offset: this.props.offset,
        };

        // Get data for all Locations or for selected Location
        let resource;
        if (locationId) {
            params.location_id = locationId;
            errorMessage = Config.fuse.error.error + ' Sales Comparison data for Location ' + locationId + '.';
            errorType = 'location';
            resource = reportsResources.SalesComparisonLocationByDate.GET_LIST(params);
        } else {
            resource = reportsResources.SalesComparisonByDate.GET_LIST(params);
        }

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(yearType, index, start, end, locationId, result.data.payload);
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
            } else if (error.request) {
                // No response
            } else {
                console.log('Error', error.message);
            }
            if (locationId) {
                this.setState({
                    detailList: [],
                    detailTableData: [],
                    detailComplete: false,
                    loadingData: false
                });
            } else {
                this.setState({
                    summaryList: [],
                    summaryTableData: [],
                    summaryComplete: false,
                    loadingData: false
                });
            }
            if (typeof this.props.onError === 'function') {
                this.props.onError(errorMessage, errorType);
            }
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store base and compared results
     *
     * @param yearType
     * @param index
     * @param start
     * @param end
     * @param locationId
     * @param data
     */
    processData(yearType, index, start, end, locationId, data) {

        // Note currently stored values
        const yearsList = this.props.yearsList;
        let totalYears = this.state.totalYears;
        let loadIndex = this.state.loadIndex;
        let results, resultsTotal;
        const emptyCurrency = toCurrency(0);

        if (data.aggregations) {

            // Process data for all Locations
            if (!locationId) {

                let summaryList = copyObjectArray(this.state.summaryList);

                if (data.aggregations.by_location && data.aggregations.by_location.buckets) {
                    results = data.aggregations.by_location.buckets;
                    resultsTotal = results.length;
                    if (resultsTotal) {
                        for (let s = 0; s < resultsTotal; s++) {

                            // Retrieve applicable data
                            let sLocationResult = results[s];
                            let sGross = sLocationResult.gross_year.value;
                            let sNet = sLocationResult.net_year.value;
                            let sAverage = sLocationResult.avg_order.value;
                            let sLocationSource = sLocationResult.column_name.hits.hits[0]._source.rxtxMsg;
                            if (sLocationSource) {
                                let sLocation = sLocationSource.payload.location;

                                // Check if this Location row already exists in the table
                                const summaryIdx = summaryList.findIndex(i => i.location_id.toString() === sLocation.id.toString());
                                if (summaryIdx === -1) {

                                    // New Location: Add row to the Table Summary list
                                    const sNewRow = {
                                        location_id: (sLocation.id) ? sLocation.id.toString() : '----',
                                        location_name: (sLocation.name) ? sLocation.name : '(None)',
                                        dba_name: (sLocation.dba && sLocation.dba.name) ? sLocation.dba.name : '(None)',
                                        state: (sLocation.state) ? sLocation.state : '--',
                                        zipcode: (sLocation.zipcode) ? sLocation.zipcode : '(None)'
                                    };
                                    if (yearType === 'base') {
                                        // The base range populates base columns
                                        sNewRow.baseGross = (sGross) ? (toCurrency(sGross / 100)) : emptyCurrency;
                                        sNewRow.baseNet = (sNet) ? (toCurrency(sNet / 100)) : emptyCurrency;
                                        sNewRow.baseAverage = (sAverage) ? (toCurrency(sAverage / 100)) : emptyCurrency;
                                        // Stub rows for each Comparison year
                                        for (let sbc = 1; sbc < totalYears; sbc++) {
                                            sNewRow['compareGross' + sbc] = emptyCurrency;
                                            sNewRow['compareNet' + sbc] = emptyCurrency;
                                            sNewRow['compareAverage' + sbc] = emptyCurrency;
                                        }
                                    } else {
                                        // Compare years only create new rows if the base range had no results for this Location
                                        sNewRow.baseGross = emptyCurrency;
                                        sNewRow.baseNet = emptyCurrency;
                                        sNewRow.baseAverage = emptyCurrency;
                                        // Stub rows for each Comparison year
                                        for (let scc = 1; scc < totalYears; scc++) {
                                            if (scc === loadIndex) {
                                                sNewRow['compareGross' + scc] = (sGross) ? (toCurrency(sGross / 100)) : emptyCurrency;
                                                sNewRow['compareNet' + scc] = (sNet) ? (toCurrency(sNet / 100)) : emptyCurrency;
                                                sNewRow['compareAverage' + scc] = (sAverage) ? (toCurrency(sAverage / 100)) : emptyCurrency;
                                            } else {
                                                sNewRow['compareGross' + scc] = emptyCurrency;
                                                sNewRow['compareNet' + scc] = emptyCurrency;
                                                sNewRow['compareAverage' + scc] = emptyCurrency;
                                            }
                                        }
                                    }
                                    summaryList.push(sNewRow);
                                } else {

                                    // Existing Location: Update existing Table Summary row with current Compare year's data
                                    const sExistingRow = summaryList[summaryIdx];
                                    // Create dynamic key values
                                    sExistingRow['compareGross' + loadIndex] = (sGross) ? (toCurrency(sGross / 100)) : emptyCurrency;
                                    sExistingRow['compareNet' + loadIndex] = (sNet) ? (toCurrency(sNet / 100)) : emptyCurrency;
                                    sExistingRow['compareAverage' + loadIndex] = (sAverage) ? (toCurrency(sAverage / 100)) : emptyCurrency;
                                }
                            }
                        }
                    } else {
                        // No results for selected date range
                    }
                } else {
                    // Structure does not conform
                }

                // Update status and retrieve next set if needed
                loadIndex++;
                if (loadIndex >= totalYears) {
                    // Update table summary and stop loading
                    this.setState({
                        loadIndex: loadIndex,
                        loadingData: false,
                        loadingMessage: '',
                        summaryComplete: true,
                        summaryList: summaryList,
                        summaryTableData: summaryList
                    });
                    this.props.onSummaryComplete(true);
                } else {
                    // Update loading status, table summary, and get next set of years to compare
                    this.setState({
                        loadIndex: loadIndex,
                        summaryList: summaryList,
                        summaryTableData: summaryList
                    }, () => {
                        this.fetchData('compare', loadIndex, yearsList[loadIndex].start, yearsList[loadIndex].end, null);
                    });
                }
            }

            // Process data for all dates for a single Location

            if (locationId) {
                let detailList = copyObjectArray(this.state.detailList);
                if (data.aggregations.column_name.hits.hits && data.aggregations.column_name.hits.hits.length) {

                    // Check that the retrieved Location matches the requested Location
                    let dLocationResult = data.aggregations.column_name.hits.hits[0]._source.rxtxMsg.payload.location;
                    if (dLocationResult.id.toString() === this.state.detailLocation.id.toString()) {

                        // Check for date data
                        if (data.aggregations.sales_per_day && data.aggregations.sales_per_day.buckets) {
                            results = data.aggregations.sales_per_day.buckets;
                            resultsTotal = results.length;
                            if (resultsTotal) {
                                for (let d = 0; d < resultsTotal; d++) {

                                    // Retrieve applicable data
                                    let dSalesResult = results[d];
                                    let dKey = dSalesResult.key_as_string;
                                    let keyDate = new Date(dSalesResult.key_as_string);
                                    let keyArr = keyDate.toLocaleDateString().split('/');
                                    let dDate = addLeadingZero(parseInt(keyArr[0], 10)) + '-' + addLeadingZero(parseInt(keyArr[1], 10)) + '-' + keyArr[2];
                                    let dGross = dSalesResult.gross_year.value;
                                    let dNet = dSalesResult.net_year.value;
                                    let dAverage = dSalesResult.avg_order.value;

                                    // Check if this Date row already exists in the table
                                    const detailIdx = detailList.findIndex(i => i.key === dSalesResult.key_as_string);
                                    if (detailIdx === -1) {

                                        // New Date: Add row to the Detail table Summary list
                                        const dNewRow = {
                                            date: (dDate) ? dDate : '(No Date)',
                                            key: (dKey) ? dKey : 'key-' + detailIdx + '-' + d,
                                            baseGross: (dGross) ? (toCurrency(dGross / 100)) : emptyCurrency,
                                            baseNet: (dNet) ? (toCurrency(dNet / 100)) : emptyCurrency,
                                            baseAverage: (dAverage) ? (toCurrency(dAverage / 100)) : emptyCurrency
                                        };
                                        if (yearType === 'base') {
                                            dNewRow.baseGross = (dGross) ? (toCurrency(dGross / 100)) : emptyCurrency;
                                            dNewRow.baseNet = (dNet) ? (toCurrency(dNet / 100)) : emptyCurrency;
                                            dNewRow.baseAverage = (dAverage) ? (toCurrency(dAverage / 100)) : emptyCurrency;
                                            // Stub rows for each Comparison year
                                            for (let lbc = 1; lbc < totalYears; lbc++) {
                                                dNewRow['compareGross' + lbc] = emptyCurrency;
                                                dNewRow['compareNet' + lbc] = emptyCurrency;
                                                dNewRow['compareAverage' + lbc] = emptyCurrency;
                                            }
                                        } else {
                                            // Compare years only create new rows if the base range had no results for this Location
                                            dNewRow.baseGross = emptyCurrency;
                                            dNewRow.baseNet = emptyCurrency;
                                            dNewRow.baseAverage = emptyCurrency;
                                            // Stub rows for each Comparison year
                                            for (let lcc = 1; lcc < totalYears; lcc++) {
                                                if (lcc === loadIndex) {
                                                    dNewRow['compareGross' + lcc] = (dGross) ? (toCurrency(dGross / 100)) : emptyCurrency;
                                                    dNewRow['compareNet' + lcc] = (dNet) ? (toCurrency(dNet / 100)) : emptyCurrency;
                                                    dNewRow['compareAverage' + lcc] = (dAverage) ? (toCurrency(dAverage / 100)) : emptyCurrency;
                                                } else {
                                                    dNewRow['compareGross' + lcc] = emptyCurrency;
                                                    dNewRow['compareNet' + lcc] = emptyCurrency;
                                                    dNewRow['compareAverage' + lcc] = emptyCurrency;
                                                }
                                            }
                                        }
                                        detailList.push(dNewRow);
                                    } else {

                                        // Existing Date: Update existing Detail Table Summary row with Compare columns
                                        const dExistingRow = detailList[detailIdx];
                                        // Create dynamic key values
                                        dExistingRow['compareGross' + loadIndex] = (dGross) ? (toCurrency(dGross / 100)) : emptyCurrency;
                                        dExistingRow['compareNet' + loadIndex] = (dNet) ? (toCurrency(dNet / 100)) : emptyCurrency;
                                        dExistingRow['compareAverage' + loadIndex] = (dAverage) ? (toCurrency(dAverage / 100)) : emptyCurrency;
                                    }
                                }
                            } else {
                                // No results for selected date range
                            }
                        } else {
                            // Structure does not conform
                        }
                    } else {
                        // Location does not match
                    }
                }

                // Update status and retrieve next set if needed
                loadIndex++;
                if (loadIndex >= totalYears) {
                    // Update detail table summary and stop loading
                    this.setState({
                        allowLocationSelection: true,
                        loadIndex: loadIndex,
                        loadingData: false,
                        loadingMessage: '',
                        detailComplete: true,
                        detailList: detailList,
                        detailTableData: detailList
                    }, () => {
                        this.scrollToDetailTable();
                    });
                    this.props.onDetailUpdate('complete');
                } else {
                    // Update loading status, detail table summary, and get next set of years to compare
                    this.setState({
                        loadIndex: loadIndex,
                        detailList: detailList,
                        detailTableData: detailList
                    }, () => {
                        this.fetchData('compare', loadIndex, yearsList[loadIndex].start, yearsList[loadIndex].end, locationId);
                    });
                }
            }
        }
    }

    /* Table Building ------------------------------ */

    /**
     * Store selected Location and make calls
     *
     * @param event
     * @param row
     */
    handleLocationSelect(event, row) {
        const location = {
            id: row.location_id,
            name: row.location_name,
            dba_name: row.dba_name,
            city: row.city,
            selected: true,
            state: row.state,
            zipcode: row.zipcode
        };
        this.setState({
            allowLocationSelection: false,
            detailLocation: location
        }, () => {
            this.scrollToDetailTable();
            this.makeLocationDataCalls(location.id);
        });
    }

    scrollToDetailTable() {
        window.scrollTo(0, this.detailPosition.offsetTop);
    }

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param tableName
     * @param table
     */
    handleSortOrFilterChange(event, tableName, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            if (tableName === 'detail') {
                this.setState({
                    detailTableData: tableData
                });
            } else {
                this.setState({
                    summaryTableData: tableData
                });
            }
        }
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            endDate,
            startDate,
            system,
            yearsList
        } = this.props;

        const {
            allowLocationSelection,
            detailLocation,
            detailList,
            detailTableData,
            detailComplete,
            loadingData,
            loadingMessage,
            summaryList,
            summaryTableData,
            summaryComplete,
            totalYears
        } = this.state;

        const baseDateString = convertStartEndDatesForFile(startDate, endDate);
        const summaryDefaultSort = setDefaultTableSort({field: 'location_id', order: 'ASC'});
        const detailDefaultSort = setDefaultTableSort({field: 'date', order: 'ASC'});

        // ReactTable headers and columns for Base and Location Detail Tables
        const summaryTableColumns = getSalesComparisonSummaryColumns(totalYears, yearsList, summaryList, summaryTableData, this.handleLocationSelect, allowLocationSelection);
        const detailTableColumns = getSalesComparisonDetailColumns(totalYears, yearsList, detailList, detailTableData);

        // CSV Table Headers for Base and Location Detail
        const csvSummaryTableHeaders = getSalesComparisonCSVHeaders('summary', totalYears, yearsList);
        const csvDetailTableHeaders = getSalesComparisonCSVHeaders('detail', totalYears, yearsList);

        // Assemble Location Detail information
        const locationCity = (detailLocation && detailLocation.city) ? detailLocation.city + ', ' : '';
        const locationState = (detailLocation && detailLocation.state) ? detailLocation.state + ' ' : '';
        const locationZip = (detailLocation && detailLocation.zipcode) ? detailLocation.zipcode : '';

        return (
            <div>
                {summaryComplete &&
                <div className="reports-table-container">

                    {/* Table Header: Sales Comparison (All Locations) */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container report-header-button-container">
                            {/* Export Sales Comparison */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={commonStyles.headerButton}
                            >
                                <ExportIcon style={commonStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={summaryTableData}
                                    headers={csvSummaryTableHeaders}
                                    filename={'Sales-Comparison-' + system + '-' + baseDateString + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={commonStyles.headerButtonLink}
                                >
                                    Export Sales Comparison
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <SummaryTableIcon style={commonStyles.summaryIcon}/>
                            <span>Sales Comparison</span>
                        </h3>
                    </div>

                    {/* Table: Sales Comparison (All Locations) */}
                    <ReactTable
                        key="table-sales-comparison-summary"
                        data={summaryList}
                        columns={summaryTableColumns}
                        defaultPageSize={10}
                        showPagination={true}
                        defaultSorted={summaryDefaultSort}
                        defaultSortDesc={true}
                        filterable
                        defaultFilterMethod={filterCaseInsensitive}
                        onSortedChange={(e) => this.handleSortOrFilterChange(e, 'summary', this.summaryReactTable)}
                        onFilteredChange={(e) => this.handleSortOrFilterChange(e, 'summary', this.summaryReactTable)}
                        ref={(r) => this.summaryReactTable = r}
                        resizable={false}
                        minRows={1}
                        className="-striped overflow no-print"
                    />
                    <ReactTable
                        key="table-sales-comparison-summary-print"
                        data={summaryTableData}
                        columns={summaryTableColumns}
                        defaultPageSize={10000}
                        showPagination={false}
                        defaultSorted={summaryDefaultSort}
                        defaultSortDesc={true}
                        filterable
                        resizable={false}
                        minRows={1}
                        className="print-only"
                    />
                </div>
                }

                {detailComplete &&
                <div className="reports-table-container">

                    {/* Table Header: Sales Comparison (Selected Location) */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container report-header-button-container">
                            {/* Export Sales Comparison (Selected Location) */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={commonStyles.headerButton}
                            >
                                <ExportIcon style={commonStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={detailTableData}
                                    headers={csvDetailTableHeaders}
                                    filename={'Sales-Comparison-Location-' + system + '-' + detailLocation.id + '-' + baseDateString + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={commonStyles.headerButtonLink}
                                >
                                    Export Date Comparisons for Location {detailLocation.id}
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <DetailTableIcon style={commonStyles.summaryIcon}/>
                            <span>Sales Comparison by Date for Location {detailLocation.id}</span>
                        </h3>
                        <div style={commonStyles.tableDetail}>
                            Location Name: {detailLocation.name}<br/>
                            DBA: {detailLocation.dba_name}<br/>
                            {locationCity}{locationState}{locationZip}
                        </div>
                    </div>

                    {/* Table: Sales Comparison (Selected Location) */}
                    <ReactTable
                        key="table-sales-comparison-detail"
                        data={detailList}
                        columns={detailTableColumns}
                        defaultPageSize={10}
                        showPagination={true}
                        defaultSorted={detailDefaultSort}
                        filterable
                        defaultFilterMethod={filterCaseInsensitive}
                        onSortedChange={(e) => this.handleSortOrFilterChange(e, 'detail', this.detailReactTable)}
                        onFilteredChange={(e) => this.handleSortOrFilterChange(e, 'detail', this.detailReactTable)}
                        ref={(r) => this.detailReactTable = r}
                        resizable={false}
                        minRows={1}
                        className="-striped overflow no-print"
                    />
                    <ReactTable
                        key="table-sales-comparison-detail-print"
                        data={detailTableData}
                        columns={detailTableColumns}
                        defaultPageSize={10000}
                        showPagination={false}
                        defaultSorted={detailDefaultSort}
                        filterable
                        resizable={false}
                        minRows={1}
                        className="print-only"
                    />

                </div>
                }

                {loadingData &&
                <div style={commonStyles.loaderContainerLeft}>
                    <h4>{loadingMessage}</h4>
                    <CircularProgress
                        size={24}
                        thickness={2}
                        className="loader"
                    />
                </div>
                }

                <div ref={(ref) => this.detailPosition = ref}>
                </div>

            </div>
        )
    }
}

SalesComparisonReport.propTypes = {
    addField: PropTypes.bool,
    endDate: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onDetailUpdate: PropTypes.func,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
    yearsList: PropTypes.array
};

SalesComparisonReport.defaultProps = {
    addField: true,
    endDate: null,
    offset: null,
    startDate: null,
    yearsList: []
};

const enhance = compose(
    connect()
);

export default enhance(SalesComparisonReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/SalesComparisonReport.js